import { useState, useEffect, useCallback } from 'react';
import { orgsData as fallbackOrgs, materialsData as fallbackMaterials, incidentsData as fallbackIncidents } from '../components/library/libraryData';

const API_BASE = '/api';
const CACHE_TTL = 60000; // 1 minute cache

// Simple in-memory cache
const cache = {
  orgs: { data: null, timestamp: 0 },
  materials: { data: null, timestamp: 0 },
  incidents: { data: null, timestamp: 0 },
};

function isCacheValid(key) {
  return cache[key].data && (Date.now() - cache[key].timestamp < CACHE_TTL);
}

async function fetchWithFallback(endpoint, fallbackData, cacheKey) {
  // Check cache first
  if (isCacheValid(cacheKey)) {
    return cache[cacheKey].data;
  }

  try {
    const response = await fetch(`${API_BASE}/${endpoint}`, {
      credentials: 'same-origin',
      signal: AbortSignal.timeout(5000)
    });
    if (response.ok) {
      const data = await response.json();
      cache[cacheKey] = { data, timestamp: Date.now() };
      return data;
    }
    throw new Error(`API returned ${response.status}`);
  } catch (error) {
    console.warn(`API fetch failed for ${endpoint}, using fallback:`, error.message);
    return fallbackData;
  }
}

// Invalidate cache after admin edits
export function invalidateCache(key) {
  if (key) {
    cache[key] = { data: null, timestamp: 0 };
  } else {
    Object.keys(cache).forEach(k => {
      cache[k] = { data: null, timestamp: 0 };
    });
  }
}

export function useOrgs() {
  const [orgs, setOrgs] = useState(fallbackOrgs);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      invalidateCache('orgs');
      const data = await fetchWithFallback('orgs', fallbackOrgs, 'orgs');
      setOrgs(data);
      setError(null);
    } catch (e) {
      setError(e.message);
      setOrgs(fallbackOrgs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWithFallback('orgs', fallbackOrgs, 'orgs')
      .then(data => { setOrgs(data); setLoading(false); })
      .catch(() => { setOrgs(fallbackOrgs); setLoading(false); });
  }, []);

  return { orgs, loading, error, refetch, setOrgs };
}

export function useMaterials() {
  const [materials, setMaterials] = useState(fallbackMaterials);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    invalidateCache('materials');
    const data = await fetchWithFallback('materials', fallbackMaterials, 'materials');
    setMaterials(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchWithFallback('materials', fallbackMaterials, 'materials')
      .then(data => { setMaterials(data); setLoading(false); });
  }, []);

  return { materials, loading, refetch, setMaterials };
}

export function useIncidents() {
  const [incidents, setIncidents] = useState(fallbackIncidents);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    invalidateCache('incidents');
    const data = await fetchWithFallback('incidents', fallbackIncidents, 'incidents');
    setIncidents(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchWithFallback('incidents', fallbackIncidents, 'incidents')
      .then(data => { setIncidents(data); setLoading(false); });
  }, []);

  return { incidents, loading, refetch, setIncidents };
}

// Helper: update a single org via API
export async function updateOrg(id, changes) {
  const response = await fetch(`${API_BASE}/orgs/${id}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  if (!response.ok) throw new Error(`Update failed: ${response.status}`);
  invalidateCache('orgs');
  return response.json();
}

// Helper: create a new org via API
export async function createOrg(orgData) {
  const response = await fetch(`${API_BASE}/orgs`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orgData),
  });
  if (!response.ok) throw new Error(`Create failed: ${response.status}`);
  invalidateCache('orgs');
  return response.json();
}

// Helper: delete an org via API
export async function deleteOrg(id) {
  const response = await fetch(`${API_BASE}/orgs/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Delete failed: ${response.status}`);
  invalidateCache('orgs');
  return response.json();
}

// Helper: update a single incident via API
export async function updateIncident(id, changes) {
  const response = await fetch(`${API_BASE}/incidents/${id}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  if (!response.ok) throw new Error(`Update failed: ${response.status}`);
  invalidateCache('incidents');
  return response.json();
}

// Helper: create a new incident via API
export async function createIncident(incidentData) {
  const response = await fetch(`${API_BASE}/incidents`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(incidentData),
  });
  if (!response.ok) throw new Error(`Create failed: ${response.status}`);
  invalidateCache('incidents');
  return response.json();
}

// Helper: update a single material via API
export async function updateMaterial(id, changes) {
  const response = await fetch(`${API_BASE}/materials/${id}`, {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(changes),
  });
  if (!response.ok) throw new Error(`Update failed: ${response.status}`);
  invalidateCache('materials');
  return response.json();
}

// Helper: create a new claim topic via API
export async function createClaim(claimData) {
  const response = await fetch(`${API_BASE}/claims`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(claimData),
  });
  if (!response.ok) throw new Error(`Create failed: ${response.status}`);
  return response.json();
}

// Helper: create a new material via API
export async function createMaterial(materialData) {
  const response = await fetch(`${API_BASE}/materials`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(materialData),
  });
  if (!response.ok) throw new Error(`Create failed: ${response.status}`);
  invalidateCache('materials');
  return response.json();
}
